
import { Options, Vue, prop } from "vue-class-component";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import CustomDropdown from "@/app/ui/components/custom-dropdown/index.vue";
import { ShiftRow, CourierDataShift, Shift } from "@/domain/entities/ManageArea";
import { ManageAreaController } from "@/app/ui/controllers/ManageAreaController";
import { ListAvailableCourierApiRequest } from "@/data/payload/api/ManageAreaApiRequest";
import { debounce } from "lodash";
import { Confirmable } from '@/decorators/confirmable';
import { ModalMessageEntities } from '@/domain/entities/MainApp';

class Props {
  isShiftEnabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  existingShift = prop<Shift[]>({
    default: [],
    type: Array
  });
}

@Options({
  components: {
    SelectSearch,
    CustomDropdown
  },
  emits: ['shifts-change', 'validate-error']
})
export default class ShiftManagement extends Vue.with(Props) {
  shifts: ShiftRow[] = [];
  timeOptions: string[] = [];
  timeOptionsFormatted: any[] = [];
  showDurationAlert = false;
  isNotError = true;

  mounted() {
    this.generateTimeOptions();
    if (this.existingShift.length) {
      this.initializeExistingShift();
    } else {
      this.initializeDefaultShift();
    }
  }

  get isShiftHas23() {
    return this.shifts.find(shift => shift.endTime === "23:00")
  }

  get isAllShiftValid() {
    return this.shifts.every(shift => shift.courierId && shift.startTime && shift.endTime);
  }

  // Generate 1-hour interval time options
  generateTimeOptions() {
    this.timeOptions = [];
    this.timeOptionsFormatted = [];
    
    for (let hour = 1; hour <= 23; hour++) {
      const timeStr = `${String(hour).padStart(2, "0")}:00`;
      this.timeOptions.push(timeStr);
      this.timeOptionsFormatted.push({
        name: timeStr,
        value: timeStr
      });
    }
  }

  async initializeExistingShift() {
    this.shifts = this.existingShift.map((item: Shift, index: number) => {
      const isLastIndex = index === this.existingShift.length - 1;
      const oneArray = this.existingShift.length === 1;
      return new ShiftRow({
        courierId: item.courierId,
        courierName: item.courierName,
        startTime: item.startTime,
        endTime: item.endTime,
        selectedCourier: new CourierDataShift({
          id: item.courierId,
          name: item.courierName,
          displayName: `[${item.courierId}] ${item.courierName}`
        }),
        endTimeEditable: !oneArray && !isLastIndex,
        selectedEndTime:
          !oneArray && !isLastIndex
            ? { name: item.endTime, value: item.endTime }
            : { name: "", value: "" }
      });
    });
    // Hitung total durasi setelah inisialisasi
    this.calculateTotalDuration();
    // Validasi keseluruhan form
    this.validateForm();
  }

  // Initialize default shift (00:00 - 23:59)
  async initializeDefaultShift() {
    const newShift = new ShiftRow({
      id: this.generateUniqueId(),
      startTime: "00:00",
      endTime: "23:59",
      courierCurrentPage: 1,
      courierHasMore: true,
      courierIsLoading: false,
    })
    this.shifts = [
      newShift
    ];
    
    // Hitung total durasi setelah inisialisasi
    this.calculateTotalDuration();
    // Validasi keseluruhan form
    this.validateForm();
  }

  generateUniqueId() {
    return `shift-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Convert time string to minutes
  timeToMinutes(time: string): number {
    const [hours, minutes] = time ? time.split(":").map(Number) : [0, 0];
    return hours * 60 + minutes;
  }

  // Validate end time
  validateEndTime(index: number, selectedEndTime: string): string {
    const shift = this.shifts[index];
    const startMinutes = this.timeToMinutes(shift.startTime);
    const endMinutes = this.timeToMinutes(selectedEndTime);

    // Validasi 1: Jam berakhir tidak boleh lebih kecil/sama dengan jam mulai
    if (endMinutes <= startMinutes) {
      return "Jam Berakhir tidak boleh lebih kecil/sama dengan Jam Mulai";
    }

    // Validasi 2: Cek apakah jika shift hanya ada dua dan user mengambil 23:59
    if (this.shifts.length === 2 && endMinutes === this.timeToMinutes("23:59")) {
      return "Jam shift sudah diambil";
    }

    // Validasi3: Cek jika yang dirubah adalah item ke dua dari bawah, maka tidak perlu ada validasi lebih lanjut
    if(index === this.shifts.length - 2) {
      if(endMinutes === this.timeToMinutes("23:59")) {
        return "Jam shift sudah diambil"; 
      }

      return ""
    };

    // Validasi 4: Jam shift sudah diambil (overlap dengan shift lain)
    // Cek apakah waktu berakhir yang dipilih berada dalam range shift lain
    for (let i = 0; i < this.shifts.length; i++) {
      const otherShift = this.shifts[i];
      const otherStartMinutes = this.timeToMinutes(otherShift.startTime);
      const otherEndMinutes = this.timeToMinutes(otherShift.endTime);
      
      // Cek Jam berakhir tidak boleh sama dengan jam mulai
      if (endMinutes === otherEndMinutes) {
        return "Jam shift sudah diambil";
      }

      // Cek apakah selectedEndTime lebih dari selected endtime yang lain namun dengan kondisi otherStartTime belum di pick
      if (i !== 0 && this.shifts.length > 2 && otherEndMinutes > 0 && endMinutes > otherEndMinutes) {
        return "Jam shift sudah diambil";
      }
      
      if (i === index) continue; // Skip shift yang sedang diedit
      
      if (!otherShift.startTime || !otherShift.endTime) continue;

      // Cek apakah selectedEndTime berada di dalam range shift lain
      // Range: (otherStartMinutes, otherEndMinutes)
      if (this.shifts.length > 2 && endMinutes === otherEndMinutes) {
        return "Jam shift sudah diambil";
      }

      if (this.shifts.length > 2 && endMinutes > otherStartMinutes && endMinutes < otherEndMinutes) {
        return "Jam shift sudah diambil";
      }
      
      // Cek juga apakah range baru akan menutupi shift lain sepenuhnya
      if (startMinutes < otherStartMinutes && endMinutes > otherEndMinutes) {
        return "Jam shift sudah diambil";
      }
      
      // Cek apakah startTime berada dalam range shift lain
      if (startMinutes > otherStartMinutes && startMinutes < otherEndMinutes) {
        return "Jam shift sudah diambil";
      }
    }

    return "";
  }

  // Validate Form - Check semua kondisi error
  validateForm() {
    // 1. Cek apakah ada error pada endTime
    const hasEndTimeError = this.shifts.some(shift => shift.endTimeError && shift.endTimeError !== "");
    
    // 2. Cek apakah semua shift memiliki startTime dan endTime yang tidak kosong
    const allTimesComplete = this.shifts.every(shift => 
      shift.startTime && 
      shift.startTime !== "" && 
      shift.endTime && 
      shift.endTime !== ""
    );
    
    // 3. Cek apakah semua courier sudah dipilih (courierId tidak null dan tidak 0)
    const allCouriersSelected = this.shifts.every(shift => 
      shift.courierId !== null && 
      shift.courierId !== 0
    );
    
    // 4. Cek apakah total durasi sudah benar (1439 menit)
    const totalMinutes = this.calculateTotalDurationValue();
    const isDurationCorrect = totalMinutes === 1439;
    
    // 5. Cek apakah shift dimulai dari 00:00
    const startsFromMidnight = this.shifts.length > 0 && this.shifts[0].startTime === "00:00";
    
    // 6. Cek apakah shift terakhir berakhir di 23:59
    const endsAtMidnight = this.shifts.length > 0 && this.shifts[this.shifts.length - 1].endTime === "23:59";
    
    // isNotError = true jika SEMUA kondisi terpenuhi
    this.isNotError = 
      !hasEndTimeError &&           // Tidak ada error validasi waktu
      allTimesComplete &&            // Semua waktu terisi
      allCouriersSelected &&         // Semua courier terisi
      isDurationCorrect &&           // Total durasi benar
      startsFromMidnight &&          // Mulai dari 00:00
      endsAtMidnight;                // Berakhir di 23:59
    
    // Emit event untuk check apakah ada error
    this.$emit("validate-error", this.isNotError);
  }

  // Add New Shift
  addShift() {
    if(this.shifts.length === 24 || this.isShiftHas23) return
    
    this.clearAllTimeErrors()

    const lastShift = this.shifts[this.shifts.length - 1];
    const newShift: ShiftRow = {
      id: this.generateUniqueId(),
      startTime: "00:00",
      endTime: "",
      startTimeEditable: false,
      endTimeEditable: true,
      endTimeExpand: false,
      selectedEndTime: {name: "", value: ""},
      courierId: 0,
      courierName: "",
      selectedCourier: new CourierDataShift(),
      courierList: [],
      courierSearchKeyword: "",
      courierCurrentPage: 1,
      courierHasMore: true,
      courierIsLoading: false,
      courierError: "",
      endTimeError: ""
    };

    if(this.shifts.length > 1) {
      newShift.startTime = this.shifts[this.shifts.length - 2].endTime
      lastShift.startTime = ""
      this.shifts.splice(this.shifts.length - 1, 0,newShift)
    } else {
      lastShift.startTime = ""
      this.shifts.unshift(newShift)
    }

    if(this.shifts.length === 2) {
      lastShift.endTime = "23:59";
      lastShift.selectedEndTime = { name: "23:59", value: "23:59" };
    }

    if (this.shifts.length > 2) {
      const previousLastIndex = this.shifts.length - 2;
      this.shifts[previousLastIndex].endTimeEditable = true;
    }

    this.calculateTotalDuration();
    this.validateForm();
    this.fetchCouriersForRow(this.shifts.length - 1);
    this.$emit('shifts-change', this.shifts)
  }

  // Delete Shift
  @Confirmable(
    new ModalMessageEntities({
      customParentButton: "w-full",
      customWrapperButton: "w-full",
      customButton: "w-full",
      customWidth: "417px",
      title: "Yakin Hapus Shift?",
      image: "badge-confirmation-general",
      message: "Hapus shift akan menghapus semua data yang telah ditambahkan.",
      textCancel: "Batal",
      textSuccess: "Simpan"
    })
  )
  deleteShift(index: number) {
    if (this.shifts.length <= 1) return;

    this.shifts.splice(index, 1);

    // Relink times and clear errors
    for (let i = 1; i < this.shifts.length; i++) {
      this.shifts[i].startTime = this.shifts[i - 1].endTime;
      this.shifts[i].endTimeError = "";
    }

    // Update editable flags
    if (this.shifts.length === 1) {
      this.shifts[0].endTimeEditable = false;
      this.shifts[0].endTime = "23:59";
      this.shifts[0].selectedEndTime = { name: "23:59", value: "23:59" };
      this.shifts[0].endTimeError = "";
    } else {
      this.shifts[0].endTimeEditable = true;
      const lastIndex = this.shifts.length - 1;
      this.shifts[lastIndex].endTime = "23:59";
      this.shifts[lastIndex].selectedEndTime = { name: "23:59", value: "23:59" };
      this.shifts[lastIndex].endTimeEditable = false;
      this.shifts[lastIndex].endTimeError = "";
    }

    this.calculateTotalDuration();
    this.validateForm();
    this.$emit('shifts-change', this.shifts)
  }

  // On Select End Time from CustomDropdown
  onSelectEndTime(index: number, selected: any) {
    const shift = this.shifts[index];
    const selectedEndTime = selected.value;

    // Validasi waktu berakhir
    const validationError = this.validateEndTime(index, selectedEndTime);
    
    if (validationError) {
      // Jika ada error, set error message dan jangan update waktu
      shift.endTimeError = validationError;
      // Kembalikan ke nilai sebelumnya atau kosongkan
      shift.selectedEndTime = shift.endTime ? { name: shift.endTime, value: shift.endTime } : { name: "", value: "" };
      this.validateForm();
      return;
    }

    // Jika validasi berhasil, clear error dan update waktu
    shift.endTimeError = "";
    shift.endTime = selectedEndTime;
    shift.selectedEndTime = selected;
    
    // Auto-fill next shift start time
    if (index < this.shifts.length - 1) {
      this.shifts[index + 1].startTime = shift.endTime;
      // Clear error pada shift berikutnya jika ada
      this.shifts[index + 1].endTimeError = "";
    }

    // Hitung ulang total durasi setiap kali ada perubahan waktu
    this.calculateTotalDuration();
    this.validateForm();
    this.$emit('shifts-change', this.shifts)
  }

  // Calculate Total Duration
  calculateTotalDuration() {
    // Cek apakah semua shift memiliki startTime dan endTime yang valid
    const allShiftsComplete = this.shifts.every(shift => 
      shift.startTime && shift.endTime && shift.startTime !== "" && shift.endTime !== ""
    );

    // Jika ada shift yang belum lengkap, tampilkan alert
    if (!allShiftsComplete) {
      this.showDurationAlert = true;
      return;
    }

    // Cek apakah shift dimulai dari 00:00
    if (this.shifts.length > 0 && this.shifts[0].startTime !== "00:00") {
      this.showDurationAlert = true;
      return;
    }

    // Cek apakah shift terakhir berakhir di 23:59
    const lastShift = this.shifts[this.shifts.length - 1];
    if (lastShift.endTime !== "23:59") {
      this.showDurationAlert = true;
      return;
    }

    // Hitung total durasi dengan menjumlahkan setiap shift
    let totalMinutes = 0;

    for (const shift of this.shifts) {
      const startMinutes = this.timeToMinutes(shift.startTime);
      const endMinutes = this.timeToMinutes(shift.endTime);

      let duration = endMinutes - startMinutes;
      
      // Handle edge case jika ada shift yang melewati midnight (seharusnya tidak ada)
      if (duration < 0) {
        duration += 1440; // 24 jam dalam menit
      }

      totalMinutes += duration;
    }

    // Total menit dari 00:00 sampai 23:59 adalah 1439 menit
    // (23 * 60 + 59 = 1439)
    const TARGET_MINUTES = 1439;

    // Tampilkan alert jika total durasi tidak sama dengan 1439 menit
    this.showDurationAlert = totalMinutes !== TARGET_MINUTES;
  }

  // Fetch Couriers for Specific Row
  async fetchCouriersForRow(index: number, isReset = false) {
    let shift = this.shifts[index];
    if (shift.courierList.length > 0 && !isReset) return;

    // Call API GET Courier List
    shift = await this.fetchCouriers(isReset, shift)
  }

  // fetching courier data from API
  async fetchCouriers(isReset = false, shift: any) {
    if (shift.courierIsLoading) return;
    if (!isReset && !shift.courierHasMore) return;

    shift.courierIsLoading = true;

    if (isReset) {
      shift.courierCurrentPage = 1;
      shift.courierList = [];
      shift.courierHasMore = true;
    }

    try {
      const params = new ListAvailableCourierApiRequest({
        page: shift.courierCurrentPage,
        limit: 20,
        q: shift.courierSearchKeyword
      });

      const result = await ManageAreaController.getAvailableCourier(params);
      
      const formattedList = result.list.map((item: any) => ({
        ...item,
        displayName: `[${item.id}] ${item.name}`
      }));

      if (result.list.length < params.limit) {
        shift.courierHasMore = false;
      }
      
      if (isReset) {
        shift.courierList = formattedList;
      } else {
        shift.courierList = [...shift.courierList, ...formattedList];
      }
      
      shift.courierCurrentPage++;
      return shift
    } catch (error) {
      console.error(error);
    } finally {
      shift.courierIsLoading = false;
    }
  }

  filterCouriersForRow = debounce((index: number, keyword: string) => {
    const shift = this.shifts[index];
    shift.courierSearchKeyword = keyword;

    this.fetchCouriers(true, shift);
  }, 500);

  loadMoreCouriersForRow(index: number) {
    const shift = this.shifts[index];
    if (!shift.courierHasMore || shift.courierIsLoading) return;

    this.fetchCouriers(false, shift)
  }

  onSelectCourier(index: number, selected: any) {
    const shift = this.shifts[index];
    if (selected) {
      shift.courierId = selected.id;
      shift.courierName = selected.name;
      shift.selectedCourier = selected;
      shift.courierError = "";
    } else {
      shift.courierId = 0;
      shift.courierName = "";
      shift.selectedCourier = new CourierDataShift();
      shift.courierError = "Kurir tidak boleh kosong!";

      if(shift.courierSearchKeyword) {
        shift.courierSearchKeyword = ""

        this.fetchCouriersForRow(index, true);
      }
    }
    
    // Validasi form setelah courier dipilih
    this.validateForm();
    this.$emit('shifts-change', this.shifts)
  }

  onCloseCourierSelect(index: number) {
    // Handle dropdown close event if needed
    const shift = this.shifts[index];

    if (!shift.selectedCourier.id) {
      if(shift.courierSearchKeyword) {
        shift.courierSearchKeyword = ""

        this.fetchCouriersForRow(index, true);
      }

      shift.courierError = "Kurir tidak boleh kosong!";
    }
    
    // Validasi form setelah dropdown ditutup
    this.validateForm();
    this.$emit('shifts-change', this.shifts)
  }

  onClearCourier(index: number) {
    const shift = this.shifts[index];
    if(shift.courierSearchKeyword) {
      shift.courierSearchKeyword = ""
      
      this.fetchCouriersForRow(index, true);
    }
    
    // Validasi form setelah dropdown ditutup
    this.validateForm();
    this.$emit('shifts-change', this.shifts)
  }

  // Get Shift Data for Submission
  getShiftData() {
    const allCouriersSelected = this.shifts.every(
      (shift) => shift.courierId !== null && shift.courierId !== 0
    );

    const totalMinutes = this.calculateTotalDurationValue();
    const errors: string[] = [];

    // Cek apakah ada error pada end time
    const hasEndTimeError = this.shifts.some(shift => shift.endTimeError);
    if (hasEndTimeError) {
      errors.push("Terdapat kesalahan pada pengaturan waktu shift");
    }

    // Total menit dari 00:00 sampai 23:59 adalah 1439 menit
    if (totalMinutes !== 1439) {
      errors.push("Total waktu shift harus 24 jam");
    }

    if (!allCouriersSelected) {
      errors.push("Kurir harus dipilih untuk semua shift");
    }

    if (errors.length === 0) {
      const formattedShifts = this.shifts.map((shift) => ({
        startTime: shift.startTime,
        endTime: shift.endTime,
        courierId: shift.courierId
      }));

      return { valid: true, data: formattedShifts };
    }

    return { valid: false, errors };
  }

  calculateTotalDurationValue() {
    let totalMinutes = 0;

    for (const shift of this.shifts) {
      if (shift.startTime && shift.endTime) {
        const startMinutes = this.timeToMinutes(shift.startTime);
        const endMinutes = this.timeToMinutes(shift.endTime);

        let duration = endMinutes - startMinutes;
        if (duration < 0) {
          duration += 1440;
        }

        totalMinutes += duration;
      }
    }

    return totalMinutes;
  }

  /**
   * Set courier error from API response
   * Method ini bisa dipanggil dari parent component menggunakan ref
   * 
   * param courierId - ID courier yang mengalami error
   * param errorMessage - Pesan error yang akan ditampilkan
   * 
   * example
   * // Di parent component:
   * this.$refs.shiftManagement.setCourierError(123, "Kurir tidak tersedia");
  */
  public setCourierError(courierId: number, errorMessage: string) {
    const shift = this.shifts.find(s => s.courierId === courierId);
    if (shift) {
      shift.courierError = errorMessage;
    }
    
    // Validasi form setelah set error
    this.validateForm();
  }

  /**
   * Clear all courier errors
   * Method ini bisa dipanggil dari parent component menggunakan ref
   *
   * // Di parent component:
   * this.$refs.shiftManagement.clearAllCourierErrors();
  */
  public clearAllCourierErrors() {
    this.shifts.forEach(shift => {
      shift.courierError = "";
    });
    
    // Validasi form setelah clear errors
    this.validateForm();
  }

  clearAllTimeErrors() {
    this.shifts.forEach(shift => {
      shift.endTimeError = "";
    });
    
    // Validasi form setelah clear errors
    this.validateForm();
  }
}
